import { ArchivedCompanyNotice } from '@/components/archived-company-notice';
import { type BreadcrumbCrumbsProps, ExportBreadcrumb } from '@/components/breadcrumbs';
import { resolveAnnotationAction } from '@/components/export/annotation-actions';
import {
  EMPTY_ANNOTATION_STATE,
  addAnnotation,
  annotationLossOnFormatSwitch,
  canRedo,
  canUndo,
  clearAllAnnotations,
  redo,
  removeAnnotation,
  undo,
} from '@/components/export/annotation-store';
import type { ExportAnnotationDraft } from '@/components/export/annotation-store';
import { ExportClientNotesField } from '@/components/export/export-client-notes-field';
import {
  DEFAULT_SUBJECT_DESCRIPTION,
  defaultExportOptions,
  legibilityWarning,
  setSubjectDescriptionPosition,
  toggleSubjectDescriptionInclude,
} from '@/components/export/export-customization';
import type { ExportOptionsState } from '@/components/export/export-customization';
import { ExportFooter } from '@/components/export/export-footer';
import { isControlVisible } from '@/components/export/export-format-controls';
import { useExportNotes } from '@/components/export/export-notes-queries';
import { ExportOptionCategories } from '@/components/export/export-option-categories';
import { ExportPresetPicker } from '@/components/export/export-preset-picker';
import { ExportPreview } from '@/components/export/export-preview';
import type { SegmentOption } from '@/components/export/export-segmented-control';
import { ExportSelectField } from '@/components/export/export-select-field';
import {
  type ExportFormat,
  type ExportSelectionState,
  allSelectedExportSelection,
  defaultExportSelection,
  isEverythingSelected,
  setActivationLevierNames,
  setIncludeClientNotes,
  toggleActivationRow,
  toggleColumn,
  toggleDimension,
} from '@/components/export/export-selection';
import {
  NO_RENDER_FACTS,
  type PdfExportRequest,
  type PlanInputs,
  planForRequest,
  useExportPreview,
} from '@/components/export/use-export-preview';
import type { ViewerTool } from '@/components/export/viewer-tool';
import { ACTIVATION_ROW_LABEL } from '@/components/matrix/matrix-labels';
import {
  cellsApi,
  companiesApi,
  matricesApi,
  matrixExportApi,
  meApi,
  triggerBrowserDownload,
} from '@/lib/api';
import { canManageCompanies } from '@/lib/company-permissions';
import { useEffectiveRole } from '@/lib/effective-role';
import { useTweakByKey } from '@/lib/tweak-store';
import { cn } from '@/lib/utils';
import {
  DEFAULT_MATRIX_VIEW_STATE,
  PALIER_NUMBERS,
  buildExportModel,
  exportHeaderThemeSchema,
  isRepeatHeadersEffective,
  palierPosition,
} from '@mss_creator/shared';
import type {
  ExportAnnotation,
  ExportRequest,
  ExportSelection,
  ExportSubjectDescriptionOptions,
  MatrixWithDimensions,
} from '@mss_creator/shared';
import { OptionTile } from '@pms-digital/design-system';
import { useMutation, useQuery } from '@tanstack/react-query';
import { createFileRoute, redirect, useNavigate } from '@tanstack/react-router';
import { useCallback, useEffect, useMemo, useState } from 'react';

export const Route = createFileRoute('/matrices/$matrixId_/export')({
  loader: async ({ params }): Promise<MatrixWithDimensions> => {
    const matrix = await matricesApi.get(params.matrixId);
    // Decision 13 (2026-08-09 design doc): a `brouillon` matrix has no cells
    // to export. Lower-risk than the grid page's own now-reverted brouillon
    // redirect (2026-08-06 doc, session 6) — the toolbar's "Exporter" button
    // only ever renders once `isGenerated` is true, so this only ever fires
    // on a direct/stale URL visit, never on a real navigation path.
    if (matrix.state !== 'généré') {
      throw redirect({ to: '/matrices/$matrixId', params: { matrixId: params.matrixId } });
    }
    return matrix;
  },
  component: ExportPage,
  pendingComponent: PendingScreen,
  errorComponent: ErrorScreen,
  staticData: { breadcrumb: ExportBreadcrumb, action: ExportBackAction },
});

/**
 * The "Retour à la matrice" link, rendered right-aligned in the centralized
 * breadcrumb strip via `staticData.action`. Defined at module scope with its
 * own `useNavigate` because `staticData` components are mounted by
 * `<Breadcrumbs>` and receive only the matched route's `params` — they share
 * no state with `ExportPage`.
 */
function ExportBackAction({ params }: BreadcrumbCrumbsProps) {
  const navigate = useNavigate();
  const matrixId = params.matrixId;
  if (matrixId === undefined) return null;
  return (
    <button
      type="button"
      onClick={() => void navigate({ to: '/matrices/$matrixId', params: { matrixId } })}
      className="shrink-0 whitespace-nowrap text-bleu-nuit text-small hover:underline"
    >
      Retour à la matrice
    </button>
  );
}

/** The selection's « Noms des leviers » modes, in the order the old radio listed them. */
const LEVIER_NAMES_OPTIONS: readonly SegmentOption<
  ExportSelectionState['activationLevierNames']
>[] = [
  { value: 'hidden', label: 'Masqués' },
  { value: 'column', label: 'Dans une colonne dédiée' },
  { value: 'inline', label: 'À la place du libellé de groupe' },
];

/** The format-switch confirmation, per what the target format drops (decision X8). */
const ANNOTATION_LOSS_MESSAGE = {
  all: 'Les annotations ne sont pas conservées dans un export Excel. Elles seront restaurées si vous revenez au PDF. Continuer ?',
  ink: 'Les dessins à main levée ne sont pas conservés dans un export HTML (les surlignages le sont). Ils seront restaurés si vous revenez au PDF. Continuer ?',
} as const;

/** An empty selection, for building the model before the real one is seeded. */
const UNSEEDED_SELECTION: ExportSelection = {
  includedColumns: [],
  includedDimensionIds: [],
  includeActivationRow: false,
  activationLevierNames: 'hidden',
  includeClientNotes: false,
};

/**
 * The PDF export request for the panel's state — one builder for both the
 * preview (which renders exactly the export) and the export button's request.
 */
function buildPdfExportRequest(input: {
  selection: ExportSelectionState;
  subjectDescription: ExportSubjectDescriptionOptions;
  annotations: readonly ExportAnnotation[];
  options: ExportOptionsState;
}): PdfExportRequest {
  return {
    format: 'pdf',
    selection: input.selection,
    subjectDescription: input.subjectDescription,
    annotations: [...input.annotations],
    page: input.options.page,
    table: input.options.table,
    dressing: input.options.dressing,
    appearance: input.options.appearance,
  };
}

/**
 * The export page (2026-08-09, 2026-08-16 and 2026-08-19 design docs, reworked
 * by export-rework chantier 2): a fixed-width single-panel sidebar — the
 * preset thumbnails pinned on top (PDF only, spec P4), the five collapsible
 * option categories scrolling beneath them (the column/dimension/activation
 * selection lives in « Contenu »), and the format picker and export button
 * pinned in a footer that never scrolls away. Excel is the entry default, so
 * the export button is always actionable. Which categories and controls each
 * format shows comes from one visibility table (`export-format-controls.ts`,
 * issue #12): Excel keeps « Tableau », « Contenu » and « Apparence », without
 * « Page » or « Habillage ». The live read-only preview fills the remaining column
 * height. A fully independent route (decision 11) — no shared chrome with the
 * grid page. Selection, customization, preview, and export generation
 * (excel/pdf/html, via `matrixExportApi.export`) are all real end-to-end.
 *
 * When the company is archived (issue #22) the page stays on screen under the
 * « Cette entreprise est archivée » notice, with « Restaurer » for the roles
 * that may: the selection, the options and the excel and html previews keep
 * working, but the backend refuses every export of such a matrix, the PDF
 * preview included, since it is the same render, so the export button is
 * disabled and the PDF preview says why instead of failing. The company is
 * read by the same query that already gates the first paint, and « Restaurer »
 * refreshes it: the page is usable again with no reload.
 */
function ExportPage() {
  const loaderMatrix = Route.useLoaderData();

  const matrixQuery = useQuery({
    queryKey: ['matrix', loaderMatrix.id],
    queryFn: () => matricesApi.get(loaderMatrix.id),
    initialData: loaderMatrix,
  });
  const matrix = matrixQuery.data;

  const companyQuery = useQuery({
    queryKey: ['company', matrix.companyId],
    queryFn: () => companiesApi.getById(matrix.companyId),
  });
  const cellsQuery = useQuery({
    queryKey: ['cells', matrix.id],
    queryFn: () => cellsApi.listForMatrix(matrix.id),
  });
  const meQuery = useQuery({ queryKey: ['me'], queryFn: meApi.get });
  const effectiveRole = useEffectiveRole(meQuery.data);
  const archived = Boolean(companyQuery.data?.archivedAt);

  const viewState = matrix.viewState ?? DEFAULT_MATRIX_VIEW_STATE;

  // Decision F3 — the export starts from the matrix page's palier header
  // theme: the tweak the grid reads, which lives in this browser's
  // localStorage where the backend cannot see it. Since chantier 2 the panel's
  // « Apparence › Thème » control owns the value, so the tweak only seeds the
  // INITIAL appearance block (the `useState` initialiser below runs once, on
  // first mount) and is never re-applied afterwards — otherwise it would
  // silently undo a preset or a manual pick.
  //
  // Parsed rather than cast (issue #8). The previous `as ExportHeaderTheme`
  // asserted an agreement nothing enforced, so when the tweak's values were
  // renamed the page went on sending a word the wire contract no longer knew
  // and every PDF/HTML export came back 400 — invisibly to the typechecker.
  // `.catch` keeps that class of failure impossible: a value this build does
  // not recognise (a stale one left in localStorage by an older build, say)
  // degrades to the shipped theme instead of breaking the export.
  const headerTheme = exportHeaderThemeSchema
    .catch('pitch')
    .parse(useTweakByKey('matrix-header-theme'));

  const [format, setFormat] = useState<ExportFormat>('excel');
  const [selection, setSelection] = useState<ExportSelectionState | null>(null);
  const [options, setOptions] = useState<ExportOptionsState>(() => {
    const defaults = defaultExportOptions();
    return { ...defaults, appearance: { ...defaults.appearance, headerTheme } };
  });
  const [subjectDescription, setSubjectDescription] = useState<ExportSubjectDescriptionOptions>(
    DEFAULT_SUBJECT_DESCRIPTION,
  );

  // Decisions A5/A7: session-only annotation state with a snapshot history.
  const [annotationState, setAnnotationState] = useState(EMPTY_ANNOTATION_STATE);
  // One exclusive pointer tool for the viewer (diagnosis B5, issue #11).
  const [viewerTool, setViewerTool] = useState<ViewerTool>('select');

  // Seeds the selection once from the matrix's visible state (decision 4/5).
  // Guarded by `selection !== null` so it never re-seeds afterward and clobbers
  // the user's own toggles. The paliers come from the matrix itself (issue #1),
  // so they are present from the loader — no waiting on the company query.
  useEffect(() => {
    if (selection !== null) return;
    setSelection(defaultExportSelection(viewState, matrix.paliers, matrix.dimensions));
  }, [selection, viewState, matrix.paliers, matrix.dimensions]);

  function handleFormatChange(nextFormat: ExportFormat) {
    // Decisions A6/S6/X8: HTML keeps the highlights but not the ink, Excel
    // keeps nothing. What is left out is *stashed*, not discarded — the page
    // state keeps it — so switching back restores it untouched, which is why
    // this asks rather than warns.
    const loss = annotationLossOnFormatSwitch(format, nextFormat, annotationState.annotations);
    if (loss !== null && !window.confirm(ANNOTATION_LOSS_MESSAGE[loss])) return;
    setFormat(nextFormat);
  }

  // Takes the selection as a parameter rather than closing over it: this sits
  // above the `selection === null` pending-screen return below (Rules of Hooks
  // — the `useMutation` that follows must run on every render, including the
  // earliest ones where the selection is still unseeded), so the closed-over
  // state would be nullable here and force a null return type on a function
  // that always has a request to build. The single null check now lives in the
  // caller, where it narrows.
  function buildExportRequest(currentSelection: ExportSelectionState): ExportRequest {
    if (format === 'excel')
      return {
        format: 'excel',
        selection: currentSelection,
        // No `dressing`: spec F1 rejected a « classeur document » (decision X1).
        table: options.table,
        appearance: options.appearance,
      };
    if (format === 'html')
      return {
        format: 'html',
        selection: currentSelection,
        subjectDescription,
        annotations: [...annotationState.annotations],
        // `page` is deliberately absent: page geometry is PDF-only on the wire
        // (spec R1) — an HTML file has no pages to lay out.
        table: options.table,
        dressing: options.dressing,
        appearance: options.appearance,
      };
    return buildPdfExportRequest({
      selection: currentSelection,
      subjectDescription,
      annotations: annotationState.annotations,
      options,
    });
  }

  // Issue #19: the notes section the html and excel previews print. The PDF
  // preview is the server's own document, so it asks for none.
  const wantsPreviewNotes = format !== 'pdf' && (selection?.includeClientNotes ?? false);
  const notesQuery = useExportNotes(matrix.id, wantsPreviewNotes);
  const previewNotes = wantsPreviewNotes ? (notesQuery.data ?? null) : null;

  // Excel and HTML: the export renders on demand, as it always has.
  const exportMutation = useMutation({
    mutationFn: async () => {
      if (selection === null) throw new Error('No selection available');
      return matrixExportApi.export(matrix.id, buildExportRequest(selection));
    },
    onSuccess: ({ blob, filename }) => {
      triggerBrowserDownload(blob, filename);
    },
  });

  // Hoisted above the `companyQuery.data`/`cellsQuery.data`/`selection` pending
  // check below (Rules of Hooks — every hook must run on every render of this
  // component instance, including the earliest ones where those queries are
  // still loading). Falls back to empty/default inputs while pending; the
  // resulting `model` is never read on that path since the component returns
  // the pending screen before reaching the JSX that uses it.
  const paliers = matrix.paliers;
  const singularitySetup = companyQuery.data?.singularitySetup ?? [];
  const modelFor = useCallback(
    (forSelection: ExportSelection) =>
      buildExportModel({
        paliers,
        dimensions: matrix.dimensions,
        cells: cellsQuery.data ?? [],
        singularitySetup,
        selection: forSelection,
      }),
    [paliers, singularitySetup, matrix.dimensions, cellsQuery.data],
  );
  const model = useMemo(() => modelFor(selection ?? UNSEEDED_SELECTION), [modelFor, selection]);

  const title = matrix.sujet ?? 'Matrice sans sujet';
  const planInputs = useMemo(
    (): PlanInputs => ({
      modelFor,
      matrixTitle: title,
      viewStateColumnWidths: viewState.columnWidths,
      viewStateRowHeights: viewState.rowHeights,
    }),
    [modelFor, title, viewState],
  );

  // The preview request *is* the export request, annotations included (issue
  // #11) — memoized, since the preview's debounce compares identities.
  const pdfRequest = useMemo(
    () =>
      selection === null
        ? null
        : buildPdfExportRequest({
            selection,
            subjectDescription,
            annotations: annotationState.annotations,
            options,
          }),
    [selection, subjectDescription, annotationState.annotations, options],
  );
  const pdfPreview = useExportPreview({
    matrixId: matrix.id,
    // The server renders what these queries last read: a refetch that brings
    // changed cells, dimensions or company dressing re-renders the preview.
    dataVersion: `${matrix.updatedAt}|${cellsQuery.dataUpdatedAt}|${companyQuery.dataUpdatedAt}`,
    request: format === 'pdf' && !archived ? pdfRequest : null,
    planInputs,
  });

  // PDF: the download is the displayed document — `flush` hands back the
  // bytes on screen when they match the current settings, and renders (and
  // displays) them first otherwise. Never a second, possibly different render.
  const pdfDownloadMutation = useMutation({
    mutationFn: () => pdfPreview.flush(),
    onSuccess: ({ blob, filename }) => {
      triggerBrowserDownload(blob, filename);
    },
  });

  // Decisions L5/L6 both need to know what the current settings actually
  // produce: the shared plan of the pending request, with the render facts of
  // the document on screen (the logo and dates only the server knows) — the
  // same derivation as the backend route and the preview's overlays.
  const displayedFacts = pdfPreview.displayed?.facts ?? NO_RENDER_FACTS;
  const pendingPlan = useMemo(
    () => (pdfRequest === null ? null : planForRequest(pdfRequest, displayedFacts, planInputs)),
    [pdfRequest, displayedFacts, planInputs],
  );
  const tiles = pendingPlan?.tiles ?? [];
  // Both hints are handed to the categories already computed, so the panel
  // never re-derives the tiling.
  const repeatHeadersEffective = isRepeatHeadersEffective(tiles);
  const legibilityNotice = legibilityWarning(
    tiles[0]?.scalePercent ?? 100,
    options.page.fontSizePt,
  );

  if (!companyQuery.data || !cellsQuery.data || selection === null) {
    return <PendingScreen />;
  }

  const orderedPaliers = [...paliers].sort(
    (a, b) => PALIER_NUMBERS.indexOf(a.number) - PALIER_NUMBERS.indexOf(b.number),
  );
  const orderedDimensions = [...matrix.dimensions].sort((a, b) => a.displayOrder - b.displayOrder);

  const everythingSelected = isEverythingSelected(selection, paliers, matrix.dimensions);

  // Selection state, not option state: the page owns it, and the categories
  // slot it under « Tableau » for every format.
  const levierNamesControl = (
    <ExportSelectField
      label="Noms des leviers"
      value={selection.activationLevierNames}
      options={LEVIER_NAMES_OPTIONS}
      disabled={!selection.includeActivationRow}
      hint={
        selection.includeActivationRow
          ? null
          : `Incluez la ${ACTIVATION_ROW_LABEL.toLowerCase()} (Contenu) pour les afficher.`
      }
      onChange={(mode) => setSelection(setActivationLevierNames(selection, mode))}
    />
  );

  return (
    // Breadcrumb strip above this route is BUILD-770's centralized <Breadcrumbs>, resolving decision 12 (2026-08-09 design doc) — it also hosts this route's "Retour à la matrice" action.
    <div className="flex h-full min-h-0 flex-col gap-4 overflow-hidden">
      {archived && (
        <div className="shrink-0">
          <ArchivedCompanyNotice
            companyId={companyQuery.data.id}
            canRestore={canManageCompanies(effectiveRole)}
            layout="banner"
          />
        </div>
      )}
      <div className="flex min-h-0 flex-1 gap-4 overflow-hidden">
        <aside className="flex min-h-0 w-72 shrink-0 flex-col">
          {/* Spec P4 — presets are whole-document PDF snapshots (page geometry
            included), so html and excel show none. */}
          {format === 'pdf' && (
            <div className="shrink-0 pb-3">
              <ExportPresetPicker options={options} onApply={setOptions} />
            </div>
          )}

          <div className="min-h-0 flex-1 overflow-y-auto pr-1">
            <ExportOptionCategories
              format={format}
              options={options}
              onChange={setOptions}
              repeatHeadersEffective={repeatHeadersEffective}
              legibilityNotice={legibilityNotice}
              levierNamesControl={levierNamesControl}
            >
              <section>
                <h4 className="mb-2 font-semibold text-caption text-gray-500 uppercase tracking-wide">
                  Colonnes
                </h4>
                <ul className="space-y-1">
                  {orderedPaliers.map((palier) => {
                    const checked = selection.includedColumns.includes(palier.number);
                    const isHiddenOnMatrix = viewState.hiddenColumns.includes(palier.number);
                    return (
                      <li key={palier.number}>
                        <label className="flex items-center justify-between gap-2 text-small">
                          <span className="flex items-center gap-2">
                            <input
                              type="checkbox"
                              checked={checked}
                              onChange={() => setSelection(toggleColumn(selection, palier.number))}
                            />
                            <span>
                              {palierPosition(palier.number)} · {palier.label}
                            </span>
                          </span>
                          {isHiddenOnMatrix && (
                            <span className="text-caption text-gray-400 italic">masquée</span>
                          )}
                        </label>
                      </li>
                    );
                  })}
                </ul>
              </section>

              <section>
                <h4 className="mb-2 font-semibold text-caption text-gray-500 uppercase tracking-wide">
                  Dimensions
                </h4>
                <ul className="space-y-1">
                  {orderedDimensions.map((dimension) => {
                    const checked = selection.includedDimensionIds.includes(dimension.id);
                    const isHiddenOnMatrix = viewState.hiddenDimensionIds.includes(dimension.id);
                    return (
                      <li key={dimension.id}>
                        <label className="flex items-center justify-between gap-2 text-small">
                          <span className="flex items-center gap-2">
                            <input
                              type="checkbox"
                              checked={checked}
                              onChange={() =>
                                setSelection(toggleDimension(selection, dimension.id))
                              }
                            />
                            <span>{dimension.title}</span>
                          </span>
                          {isHiddenOnMatrix && (
                            <span className="text-caption text-gray-400 italic">masquée</span>
                          )}
                        </label>
                      </li>
                    );
                  })}
                </ul>
                <label className="mt-2 flex items-center gap-2 border-gray-100 border-t pt-2 text-small">
                  <input
                    type="checkbox"
                    checked={selection.includeActivationRow}
                    onChange={() => setSelection(toggleActivationRow(selection))}
                  />
                  <span>Inclure {ACTIVATION_ROW_LABEL.toLowerCase()}</span>
                </label>
              </section>

              <button
                type="button"
                disabled={everythingSelected}
                onClick={() =>
                  setSelection({
                    ...allSelectedExportSelection(paliers, matrix.dimensions),
                    // « tout afficher » widens the grid selection; the notes option is not part of it.
                    includeClientNotes: selection.includeClientNotes,
                  })
                }
                className={cn(
                  'font-semibold text-bleu-nuit text-small hover:underline disabled:cursor-not-allowed disabled:text-gray-400 disabled:no-underline',
                )}
              >
                {everythingSelected ? 'tout afficher — tout est déjà visible' : 'tout afficher'}
              </button>

              <section>
                <h4 className="mb-2 font-semibold text-caption text-gray-500 uppercase tracking-wide">
                  Notes
                </h4>
                <ExportClientNotesField
                  checked={selection.includeClientNotes}
                  onChange={(include) => setSelection(setIncludeClientNotes(selection, include))}
                />
              </section>

              {/* Excel renders no sujet/description block, so it offers none. */}
              {isControlVisible(format, 'subjectDescription') && (
                <section className="space-y-2">
                  <h4 className="font-semibold text-caption text-gray-500 uppercase tracking-wide">
                    Sujet et description
                  </h4>
                  <label className="flex items-center gap-2 text-small">
                    <input
                      type="checkbox"
                      checked={subjectDescription.include}
                      onChange={() =>
                        setSubjectDescription(toggleSubjectDescriptionInclude(subjectDescription))
                      }
                    />
                    <span>Inclure le sujet et la description de la matrice</span>
                  </label>
                  {subjectDescription.include && (
                    <div
                      role="radiogroup"
                      aria-label="Position de la description"
                      className="flex flex-col gap-2"
                    >
                      {(
                        [
                          ['before', 'Avant le tableau'],
                          ['after', 'Après le tableau'],
                        ] as const
                      ).map(([value, label]) => (
                        <OptionTile
                          key={value}
                          type="radio"
                          name="subject-description-position"
                          label={label}
                          checked={subjectDescription.position === value}
                          onChange={() =>
                            setSubjectDescription(
                              setSubjectDescriptionPosition(subjectDescription, value),
                            )
                          }
                        />
                      ))}
                    </div>
                  )}
                </section>
              )}
            </ExportOptionCategories>
          </div>

          <ExportFooter
            format={format}
            onFormatChange={handleFormatChange}
            onExport={() =>
              format === 'pdf' ? pdfDownloadMutation.mutate() : exportMutation.mutate()
            }
            isExporting={
              format === 'pdf' ? pdfDownloadMutation.isPending : exportMutation.isPending
            }
            hasFailed={format === 'pdf' ? pdfDownloadMutation.isError : exportMutation.isError}
            archived={archived}
          />
        </aside>

        <div className="min-h-0 min-w-0 flex-1">
          <ExportPreview
            format={format}
            model={model}
            matrixTitle={title}
            companyName={companyQuery.data.name}
            noyau={companyQuery.data.noyau}
            matrixSujet={matrix.sujet}
            matrixContexte={matrix.contexte}
            subjectDescription={subjectDescription}
            options={options}
            columnWidths={matrix.viewState?.columnWidths ?? {}}
            rowHeights={matrix.viewState?.rowHeights ?? {}}
            selection={selection}
            pdfPreview={pdfPreview}
            pdfUnavailable={archived}
            annotations={annotationState.annotations}
            notes={previewNotes}
            tool={viewerTool}
            onToolChange={setViewerTool}
            canUndo={canUndo(annotationState)}
            canRedo={canRedo(annotationState)}
            onUndo={() => setAnnotationState(undo)}
            onRedo={() => setAnnotationState(redo)}
            onClearAll={() => setAnnotationState(clearAllAnnotations)}
            // The id is minted here rather than in the surface that drew the
            // annotation, so the gesture code stays pure and assertable.
            onAddAnnotation={(annotation: ExportAnnotationDraft) =>
              setAnnotationState((state) =>
                addAnnotation(state, { ...annotation, id: crypto.randomUUID() }),
              )
            }
            // Decision S7: erasing goes through the same rule the Édition layer
            // used — a highlight on the clicked line first, else the most recent
            // stroke anchored in the clicked cell.
            onEraseAt={(hit) =>
              setAnnotationState((state) => {
                const action = resolveAnnotationAction({
                  tool: 'erase',
                  hit: { ...hit, scalePercent: 100 },
                  annotations: state.annotations,
                });
                return action?.type === 'remove' ? removeAnnotation(state, action.id) : state;
              })
            }
            // « Aménager » writes the table's arrangement (widths, column order,
            // page breaks); the debounced preview then re-renders from it.
            onTableChange={(table) => setOptions((current) => ({ ...current, table }))}
          />
        </div>
      </div>
    </div>
  );
}

function PendingScreen() {
  return <p className="px-4 py-4 text-gray-500 text-small">Chargement…</p>;
}

function ErrorScreen() {
  return (
    <section className="space-y-2 px-4 py-4">
      <h1 className="font-bold text-danger text-h3">Erreur</h1>
      <p className="text-small">Impossible de contacter le serveur. Réessayez plus tard.</p>
    </section>
  );
}
